import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button, Card, ErrorBanner, Field } from "@/components/ui-kit";
import { useAuth } from "@/lib/auth";
import { fetchAllBorrowers, insertNotification, sendReminder } from "@/lib/data";
import type { Borrower } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/messages")({
  component: MessagesPage,
});

type Msg = {
  id: string;
  body: string;
  channel: string;
  sentAt: string;
  sentBy: string;
};

function MessagesPage() {
  const { session } = useAuth();
  const adminName = session?.kind === "admin" ? session.name : "Admin";
  const [borrowers, setBorrowers] = useState<Borrower[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState<"email" | "whatsapp" | "both" | "internal">("internal");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);

  useEffect(() => {
    fetchAllBorrowers()
      .then((rows) => {
        setBorrowers(rows);
        setSelectedId((prev) => prev || rows[0]?.id || "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load borrowers"));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    supabase
      .from("reminders")
      .select("*")
      .eq("borrower_id", selectedId)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setMessages(
          ((data ?? []) as Record<string, unknown>[]).map((m) => ({
            id: String(m.id),
            body: String(m.message ?? m.body ?? m.custom_message ?? ""),
            channel: String(m.channel ?? "email"),
            sentAt: String(m.created_at ?? m.sent_at ?? ""),
            sentBy: String(m.sent_by ?? "Admin"),
          })),
        );
      });
  }, [selectedId, toast]);

  const selected = borrowers.find((b) => b.id === selectedId);

  const send = async () => {
    setError(null);
    setToast(null);
    if (!selected) return setError("Select a borrower.");
    if (!body.trim()) return setError("Write a message.");
    setSending(true);
    try {
      if (channel === "internal") {
        await insertNotification(selected.id, `Message from ${adminName}: ${body.slice(0, 200)}`, {
          title: "New message",
          type: "message",
        });
        setToast("Internal notification sent");
      } else {
        const { data: loans } = await supabase
          .from("loans")
          .select("id")
          .eq("borrower_id", selected.id)
          .in("status", ["active", "overdue", "pending"])
          .limit(1);
        const loanId = loans?.[0]?.id;
        if (!loanId) throw new Error("Borrower has no active loan for reminder.");
        const res = await sendReminder({
          borrowerId: selected.id,
          loanId: String(loanId),
          channel: channel === "both" ? "both" : channel,
          customMessage: body,
        });
        if (res?.whatsapp_link) window.open(res.whatsapp_link, "_blank");
        setToast("Reminder sent");
      }
      setBody("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Operations</div>
        <h1 className="font-display text-4xl mt-1">Messages</h1>
        <p className="text-sm text-muted-foreground mt-1">Manually message a borrower. Automatic reminders fire 2 days before due dates.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
        <Card className="!p-0">
          <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">Borrowers</div>
          <div className="max-h-[500px] overflow-y-auto">
            {borrowers.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelectedId(b.id)}
                className={`w-full text-left px-4 py-3 border-b border-border/40 transition ${
                  selectedId === b.id ? "bg-accent text-gold" : "hover:bg-accent/40"
                }`}
              >
                <div className="font-medium">{b.name}</div>
                <div className="text-xs text-muted-foreground">{b.phone}</div>
              </button>
            ))}
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <h2 className="font-display text-xl">Send message</h2>
            <p className="text-xs text-muted-foreground mt-1">{selected ? `To: ${selected.name} (${selected.email})` : "Pick a borrower"}</p>
            <div className="mt-4 space-y-4">
              <ErrorBanner message={error} />
              {toast && (
                <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">{toast}</div>
              )}
              <div className="flex gap-2 flex-wrap">
                {(["internal", "email", "whatsapp", "both"] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setChannel(c)}
                    className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border transition ${
                      channel === c ? "border-gold text-gold bg-gold/10" : "border-border text-muted-foreground"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <Field label="Message">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  className="w-full bg-input/60 border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 transition"
                  placeholder="Short note to borrower..."
                />
              </Field>
              <Button variant="gold" loading={sending} onClick={send}>
                {channel === "whatsapp" || channel === "both" ? "Send reminder" : "Send"}
              </Button>
            </div>
          </Card>

          <Card>
            <h2 className="font-display text-xl">Message history</h2>
            {messages.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No messages yet for this borrower.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className="p-3 rounded-md border border-border bg-accent/20">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span className="uppercase tracking-wider text-gold">{m.channel}</span>
                      <span>{m.sentAt ? new Date(m.sentAt).toLocaleString() : ""}</span>
                    </div>
                    <div className="mt-2 text-sm">{m.body}</div>
                    <div className="mt-1 text-xs text-muted-foreground">— {m.sentBy}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
